import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { broadcastSettingsUpdate } from '@/hooks/useSiteSettings';

const SETTINGS_UPDATED_EVENT = 'site-settings-updated';

export function applyFaviconToDOM(url: string) {
  if (!url || url.trim() === '') return;
  ['link[rel="icon"]', 'link[rel="shortcut icon"]'].forEach(sel => {
    let el = document.querySelector(sel) as HTMLLinkElement | null;
    if (!el) {
      el = document.createElement('link');
      el.setAttribute('rel', sel.includes('shortcut') ? 'shortcut icon' : 'icon');
      document.head.appendChild(el);
    }
    el.setAttribute('href', url);
  });
}

function applyMetaToDOM(map: Record<string, string>) {
  const setMeta = (name: string, content: string, prop = false) => {
    if (!content) return;
    const attr = prop ? 'property' : 'name';
    let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  if (map.meta_title) document.title = map.meta_title;
  if (map.meta_description) setMeta('description', map.meta_description);
  if (map.meta_keywords) setMeta('keywords', map.meta_keywords);
  if (map.og_title) setMeta('og:title', map.og_title, true);
  if (map.og_description) setMeta('og:description', map.og_description, true);
  if (map.og_image) setMeta('og:image', map.og_image, true);
  if (map.site_name || map.meta_title) setMeta('og:site_name', map.site_name || map.meta_title, true);
  setMeta('twitter:card', 'summary_large_image');
  if (map.og_title) setMeta('twitter:title', map.og_title);
  if (map.og_description) setMeta('twitter:description', map.og_description);
  if (map.og_image) setMeta('twitter:image', map.og_image);
  if (map.site_favicon_url) applyFaviconToDOM(map.site_favicon_url);
}

/**
 * Fetches SEO/OG/branding settings from site_settings and injects them into <head>.
 * Also listens for live broadcast updates so saves apply instantly without reload.
 * Called once at app startup from App.tsx.
 */
export function useDynamicSEO() {
  useEffect(() => {
    // Initial fetch on mount / refresh
    supabase
      .from('site_settings')
      .select('key, value')
      .in('key', [
        'meta_title', 'meta_description', 'meta_keywords',
        'og_title', 'og_description', 'og_image', 'site_name',
        'site_favicon_url', 'site_logo_url',
      ])
      .then(({ data }) => {
        if (!data || data.length === 0) return;
        const map: Record<string, string> = {};
        data.forEach((r: { key: string; value: string }) => {
          // Store all values (including empty) so cache is complete
          map[r.key] = r.value ?? '';
        });
        // Apply meta + favicon to DOM immediately on load
        applyMetaToDOM(map);
        // Seed the shared settings cache so Sidebar / other consumers get logo instantly
        broadcastSettingsUpdate(map);
      });

    // Also re-apply whenever admin saves (no reload needed)
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<Record<string, string>>).detail;
      applyMetaToDOM(detail);
    };
    window.addEventListener(SETTINGS_UPDATED_EVENT, handler);
    return () => window.removeEventListener(SETTINGS_UPDATED_EVENT, handler);
  }, []);
}
